// components/common/NotificationBell.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getNotificationCounts } from '../../services/notificationService';
import NotificationCenter from './NotificationCenter';
import { Bell, X } from 'lucide-react';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';

const NotificationBell = () => {
  const { userData: user, userRole } = useAuth();
  const [counts, setCounts] = useState({ unreadCount: 0, totalCount: 0 });
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const prevUnreadCount = useRef(0);
  const notificationPermission = useRef(null);

  // Get notification icon by type
  const getNotificationIconByType = (type, subType) => {
    if (type === 'application') {
      const icons = {
        submitted: '📋',
        approved: '✅',
        rejected: '❌',
        under_review: '🔍',
        kyp_generated: '📄'
      };
      return icons[subType] || '📄';
    } else if (type === 'dispute') {
      const icons = {
        dispute_raised: '🚨',
        acknowledged: '✅',
        resolved: '✅',
        escalated: '⚠️'
      };
      return icons[subType] || '💬';
    } else if (type === 'reminder') {
      return '⏰';
    }
    return '🔔';
  };

  // Define notification toast function with full details
  const showNewNotificationToast = useCallback((count, notificationData) => {
    // Play notification sound
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (e) {}

    // Show browser/system notification with details
    if (notificationPermission.current === 'granted') {
      const title = notificationData ? `${getNotificationIconByType(notificationData.type, notificationData.subType)} ${notificationData.title}` : '🔔 New Notification!';
      const body = notificationData ? notificationData.message : `You have ${count} new notification${count > 1 ? 's' : ''} from Samriddhi Healthcare`;
      
      const notification = new Notification(title, {
        body: body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: 'notification-update',
        requireInteraction: true,
        silent: false,
        renotify: true,
        vibrate: [200, 100, 200],
        timestamp: Date.now(),
        data: { count, ...notificationData }
      });
      
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        setShowNotifications(true);
        notification.close();
      };

      setTimeout(() => { notification.close(); }, 10000);
    }

    // Add enhanced toast notification - only show if page is visible
    if (!document.hidden) {
      const toastId = Date.now();
      setToasts(prev => [...prev, { 
        id: toastId, 
        count, 
        message: notificationData?.message || `${count} new notification${count > 1 ? 's' : ''} received`,
        title: notificationData?.title || 'New Notification',
        type: notificationData?.type || 'general',
        subType: notificationData?.subType || '',
        data: notificationData
      }]);
      
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toastId));
      }, 6000);
    }
  }, []);

  // Define loadCounts before using it
  const loadCounts = useCallback(async (deptRecipientId = null) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const [userRes, deptRes] = await Promise.all([
        getNotificationCounts(user.uid),
        deptRecipientId ? getNotificationCounts(deptRecipientId) : Promise.resolve({ success: true, unreadCount: 0, totalCount: 0 })
      ]);
      const unread = (userRes.success ? userRes.unreadCount : 0) + (deptRes.success ? deptRes.unreadCount : 0);
      const total = (userRes.success ? userRes.totalCount : 0) + (deptRes.success ? deptRes.totalCount : 0);
      setCounts((prev) => ({ ...prev, unreadCount: unread, totalCount: total }));
    } catch (error) {
      console.error('Error loading notification counts:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        notificationPermission.current = permission;
      });
    } else if ('Notification' in window) {
      notificationPermission.current = Notification.permission;
    }
  }, []);

  useEffect(() => {
    if (!user?.uid) return;

    // Map roles to a department recipient for department-level notifications
    const roleToDept = {
      PolicyWriter: 'policy',
      FinancialTeam: 'finance',
    };
    const deptRecipientId = roleToDept[userRole] || null;

    // Initial fetch (fallback while listeners attach)
    loadCounts(deptRecipientId);

    // Real-time listeners for unread and total counts
    const unreadQ = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const totalQ = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid)
    );

    let lastNotificationTime = null;

    // Listen for latest notifications to detect new arrivals
    const latestUnreadQ = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid),
      where('read', '==', false),
      orderBy('createdAt', 'desc'),
      limit(5)
    );

    const unsubLatest = onSnapshot(latestUnreadQ, (snapshot) => {
      const latest = snapshot.docs[0];
      if (latest) {
        const notificationData = latest.data();
        const createdAt = notificationData.createdAt?.toDate();
        if (lastNotificationTime && createdAt > lastNotificationTime) {
          // Pass the actual notification data to show details
          showNewNotificationToast(1, {
            title: notificationData.title,
            message: notificationData.message,
            type: notificationData.type,
            subType: notificationData.subType,
            applicationId: notificationData.applicationId,
            priority: notificationData.priority,
            metadata: notificationData.metadata
          });
        }
        lastNotificationTime = createdAt || new Date();
      }
    });

    const unsubUnread = onSnapshot(
      query(collection(db, 'notifications'), where('recipientId', '==', user.uid), where('read', '==', false)),
      (snapshot) => {
        const newCount = snapshot.size;
        setCounts((prev) => ({ ...prev, unreadCount: newCount }));
        prevUnreadCount.current = newCount;
      },
      (error) => console.error('Realtime unread notifications error:', error)
    );

    const unsubTotal = onSnapshot(
      totalQ,
      (snapshot) => {
        setCounts((prev) => ({ ...prev, totalCount: snapshot.size }));
      },
      (error) => console.error('Realtime total notifications error:', error)
    );

    // If user is in a department role, add department-level listeners and sum counts
    let unsubDeptUnread = null;
    let unsubDeptTotal = null;
    if (deptRecipientId) {
      const deptUnreadQ = query(
        collection(db, 'notifications'),
        where('recipientId', '==', deptRecipientId),
        where('read', '==', false)
      );
      const deptTotalQ = query(
        collection(db, 'notifications'),
        where('recipientId', '==', deptRecipientId)
      );
      unsubDeptUnread = onSnapshot(
        deptUnreadQ,
        (snapshot) => {
          setCounts((prev) => ({ ...prev, unreadCount: (prev.userUnreadCount || 0) + snapshot.size }));
        },
        (error) => console.error('Dept unread notifications error:', error)
      );
      unsubDeptTotal = onSnapshot(
        deptTotalQ,
        (snapshot) => {
          setCounts((prev) => ({ ...prev, totalCount: (prev.userTotalCount || 0) + snapshot.size }));
        },
        (error) => console.error('Dept total notifications error:', error)
      );

      // Also keep internal separate counters to compute sums correctly
      const userUnreadHandler = (snap) => {
        setCounts((prev) => ({ ...prev, userUnreadCount: snap.size, unreadCount: snap.size }));
      };
      const userTotalHandler = (snap) => {
        setCounts((prev) => ({ ...prev, userTotalCount: snap.size, totalCount: snap.size }));
      };
      // Reattach user listeners with custom handlers to maintain separate counts
      try { unsubUnread(); } catch {}
      try { unsubTotal(); } catch {}
      const unsubUnreadUser = onSnapshot(unreadQ, userUnreadHandler);
      const unsubTotalUser = onSnapshot(totalQ, userTotalHandler);

      return () => {
        try { unsubUnreadUser && unsubUnreadUser(); } catch {}
        try { unsubTotalUser && unsubTotalUser(); } catch {}
        try { unsubDeptUnread && unsubDeptUnread(); } catch {}
        try { unsubDeptTotal && unsubDeptTotal(); } catch {}
      };
    }

    return () => {
      try { unsubUnread(); } catch {}
      try { unsubTotal(); } catch {}
      try { unsubLatest(); } catch {}
    };
  }, [user?.uid, userRole, showNewNotificationToast, loadCounts]);

  const handleBellClick = () => {
    setShowNotifications(true);
  };

  const handleCloseNotifications = () => {
    setShowNotifications(false);
    // Refresh counts after closing
    loadCounts();
  };

  const dismissToast = (toastId) => {
    setToasts(prev => prev.filter(t => t.id !== toastId));
  };

  if (!user) return null;

  return (
    <>
      {/* Enhanced Toast Notifications - Bottom Right */}
      <div className="fixed bottom-32 right-4 z-[9999] space-y-2">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-4 rounded-2xl shadow-2xl flex items-start gap-4 min-w-[380px] max-w-md animate-slide-up"
            style={{
              boxShadow: '0 25px 30px -5px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="flex-shrink-0 w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-2xl">
              {getNotificationIconByType(toast.type, toast.subType)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm mb-1">{toast.title}</div>
              <div className="text-xs text-blue-100 leading-relaxed">{toast.message}</div>
              {toast.data?.applicationId && (
                <div className="mt-2 text-xs text-white/70">📋 {toast.data.applicationId}</div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wide bg-white/20 px-2 py-0.5 rounded">
                  {toast.type === 'dispute' ? 'Query' : toast.type}
                </span>
                {toast.data?.priority && (
                  <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded">
                    {toast.data.priority.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => dismissToast(toast.id)}
              className="flex-shrink-0 p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>
      
      <div className="relative">
        <button
          onClick={handleBellClick}
          className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full transition-all hover:scale-110 group"
          title="Notifications"
        >
          <Bell className="w-6 h-6 transition-transform group-hover:animate-swing" />
          
          {/* Ripple effect when new notifications */}
          {counts.unreadCount > 0 && (
            <span className="absolute inset-0 rounded-full bg-red-500/20 animate-ping"></span>
          )}
          
          {/* Notification Badge */}
          {counts.unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 font-semibold shadow-lg border-2 border-white">
              {counts.unreadCount > 99 ? '99+' : counts.unreadCount}
            </span>
          )}
          
          {/* Loading indicator */}
          {loading && (
            <span className="absolute -top-1 -right-1 bg-blue-500 rounded-full w-3 h-3 animate-pulse"></span>
          )}
        </button>

        {/* Dropdown Panel anchored under the bell */}
        <NotificationCenter 
          isOpen={showNotifications} 
          onClose={handleCloseNotifications}
          mode="dropdown"
        />
      </div>
    </>
  );
};

export default NotificationBell;
